import React, {ReactNode} from "react";
import {IODirection} from "../Enum/IODirection";
import {FactoryGameType} from "../types";
import ItemImage from "./ItemImage";
import './ItemStack.sass';
import WithTooltip from "../../react/components/ui/withTooltip";
import {ItemStackContext} from "./ItemStackContext";

type Props = {
    amount: number,
    item: string,
    direction?: IODirection,
    gameType: FactoryGameType,
    children?: ReactNode,
    duration?: number,
}

function formatNumber(amount: number) {
    const localeString = (Math.round(amount * 1000) / 1000).toLocaleString('de-DE', { maximumFractionDigits: 2, minimumFractionDigits: 2 });
    if (localeString.includes(',')) {
        const [integerPart, decimalPart] = localeString.split(',');
        return <>{integerPart}<div className='itemCountDecimalPart'>,{decimalPart}</div></>;
    } else {
        return <>{localeString}</>;
    }
}

function ItemFrequency({ amount }: { amount: number }) {
    return <>{formatNumber(amount)}<sub>/min</sub></>;
}

function AbstractItemStack({amount, item, direction, gameType, children, amountChild}: Props & { amountChild: React.ReactNode }) {
    let directionClass: string;
    switch (direction) {
        case IODirection.Input:
            directionClass = 'input';
            break;
        case IODirection.Output:
            directionClass = 'output';
            break;
        default:
            directionClass = '';
            break;
    }
    // We provide the ItemStackContext, so that e.g. Buttons that get passed as children can use the context to determine what they're supposed to do.
    return <ItemStackContext.Provider value={{item, amount, direction, gameType }}>
        <div className={'itemStack ' + directionClass}>
            <div className='imageContainer'><WithTooltip tooltip={item}><ItemImage item={item} gameType={gameType} /></WithTooltip></div>
            <div className='amountContainer'>{amountChild}</div>
            <div className='childrenContainer'>{children}</div>
        </div>
    </ItemStackContext.Provider>
}

export function ItemRecipeStack(props: Props) {
    const perMinute = props.amount / (props.duration ?? 60) * 60;
    return <AbstractItemStack {...props} amountChild={<>
            <div>{formatNumber(props.amount)}</div>
            <div>{<ItemFrequency amount={perMinute} />}</div>
        </>}
    />
}

export function ItemStack(props: Props) {
    return <AbstractItemStack {...props} amountChild={formatNumber(props.amount)} />
}

export function ItemThroughput(props: Props) {
    return <AbstractItemStack {...props} amountChild={<ItemFrequency amount={props.amount} />} />
}

export type ItemStackProps = Props;